/**
 * 28-10-2026 (Jordy): filter en sortering in het blok New followers — "Online only",
 * "Online first", platform en model. Nieuwste volger eerst blijft de standaard.
 *
 * Wat "online" hier betekent: het platform meldt de fan online, en die melding is
 * hooguit ``ONLINE_VERS_MIN`` minuten oud (`fan_presence_at`). De stip zelf
 * (src/services/fan_presence.py) is de LAATSTE stand die we zagen, zonder
 * houdbaarheid: een fan wiens "offline" ons nooit bereikte, blijft daar uren of
 * dagen online staan. Gemeten op 28-10 21:45: van de FC-fans die op online
 * stonden, was bij NT 7 van de 110 en bij TT 8 van de 112 hooguit 30 minuten oud.
 *
 * - FanCentro: `fan_presence_at` is het moment waarop WIJ de stand zagen. De
 *   contactlijst van elk verbonden model ververst elke 30 s tot 5 min, dus een
 *   fan die echt online is, valt binnen 10 minuten.
 * - F2F: `fan_presence_at` is F2F's eigen `last_seen` (de chatlijst, elke ~3 min
 *   opgehaald). Binnen 10 minuten = F2F zag de fan in de laatste 10 minuten.
 *
 * Een oudere "online" telt hier als onbekend: geen groene stip, niet in het filter.
 * Liever een fan te weinig dan een chatter die op een fan wacht die er niet is.
 *
 * De keuze blijft per gebruiker in deze browser bewaard (localStorage); de regels
 * van New followers zelf (wie erin staat, tot wanneer) veranderen niet.
 *
 * 30-10-2026 (Jordy, Chats compacter): in het blok blijven alleen "Online only"
 * en "Online first". De knoppen All/FC/F2F en de modelkeuze zijn weg; het blok
 * volgt weer het filter van de pagina. Een eerder BEWAARDE platform- of
 * modelkeuze telt daarom niet meer: leesKeuze geeft altijd 'all' terug en haalt
 * die velden uit de opslag, zodat niemand vastzit in een onzichtbaar filter.
 */
import type { QueueRow } from './panels'

/** Hoe oud een online-melding mag zijn om mee te tellen (minuten). */
export const ONLINE_VERS_MIN = 10

export interface VolgerKeuze {
  /** Alleen fans die nu online zijn (zie boven). */
  alleenOnline: boolean
  /** Online fans bovenaan, daaronder de rest; binnen elke groep nieuwste eerst. */
  onlineEerst: boolean
  /** Altijd 'all' sinds 30-10-2026 (de knoppen zijn weg; zie boven). */
  platform: string
  /** Altijd 'all' sinds 30-10-2026. */
  model: string
}

export const STANDAARD_KEUZE: VolgerKeuze = {
  alleenOnline: false, onlineEerst: false, platform: 'all', model: 'all',
}

/**
 * 30-10-2026 (Jordy): hetzelfde filter staat ook in het blok VIP, met een EIGEN
 * keuze: elk blok zijn eigen sleutel. New followers houdt zijn oude sleutel.
 */
export type KeuzeBlok = 'newFollowers' | 'vip'

export function keuzeSleutel(gebruikerId: number | null | undefined,
                             blok: KeuzeBlok = 'newFollowers'): string {
  return `novahub.${blok}.filter.${gebruikerId ?? 'anon'}`
}

/** De bewaarde keuze; onbekende of kapotte waarden worden de standaard. Platform
 *  en model zijn altijd 'all'; stond er nog een oude keuze (bv. FC of een model),
 *  dan wordt die meteen uit de opslag gehaald. */
export function leesKeuze(gebruikerId: number | null | undefined,
                          blok: KeuzeBlok = 'newFollowers'): VolgerKeuze {
  let ruw: unknown = null
  try { ruw = JSON.parse(localStorage.getItem(keuzeSleutel(gebruikerId, blok)) || 'null') } catch { ruw = null }
  const r = (ruw && typeof ruw === 'object' ? ruw : {}) as Partial<Record<keyof VolgerKeuze, unknown>>
  const keuze: VolgerKeuze = {
    alleenOnline: r.alleenOnline === true,
    onlineEerst: r.onlineEerst === true,
    platform: 'all',
    model: 'all',
  }
  if (('platform' in r && r.platform !== 'all') || ('model' in r && r.model !== 'all')) {
    bewaarKeuze(gebruikerId, keuze, blok)
  }
  return keuze
}

export function bewaarKeuze(gebruikerId: number | null | undefined, k: VolgerKeuze,
                            blok: KeuzeBlok = 'newFollowers'): void {
  const schoon: VolgerKeuze = { alleenOnline: !!k.alleenOnline, onlineEerst: !!k.onlineEerst, platform: 'all', model: 'all' }
  try { localStorage.setItem(keuzeSleutel(gebruikerId, blok), JSON.stringify(schoon)) } catch { /* private mode: alleen deze sessie */ }
}

/** Meldt het platform de fan online, en is die melding hooguit ONLINE_VERS_MIN oud? */
export function isVersOnline(c: QueueRow, nu: number = Date.now()): boolean {
  if (c?.fan_online !== true) return false
  const t = Date.parse(String(c.fan_presence_at ?? ''))
  if (!Number.isFinite(t)) return false
  return nu - t <= ONLINE_VERS_MIN * 60_000
}

/** De rij zoals dit blok hem toont: een verouderde "online" wordt onbekend (geen stip). */
export function metVersStip(c: QueueRow, nu: number = Date.now()): QueueRow {
  return c.fan_online === true && !isVersOnline(c, nu) ? { ...c, fan_online: null } : c
}

const ms = (s: unknown) => {
  const t = Date.parse(String(s ?? ''))
  return Number.isFinite(t) ? t : 0
}

/**
 * De rijen van het blok.
 *
 * @param rows        de gewone lijst (nieuwste eerst, per 100 geladen)
 * @param onlineRows  de nieuwe volgers die volgens de server online staan (alle,
 *                    zonder houdbaarheid); hier blijft alleen over wat vers is
 */
export function volgerLijst(rows: QueueRow[], onlineRows: QueueRow[], k: VolgerKeuze,
                            nu: number = Date.now()): QueueRow[] {
  const nieuwsteEerst = (a: QueueRow, b: QueueRow) =>
    ms(b.nieuwe_volger_op) - ms(a.nieuwe_volger_op) || b.id - a.id
  const gezien = new Set<number>()
  const uniek = (c: QueueRow) => !!c && !gezien.has(c.id) && (gezien.add(c.id), true)
  if (k.alleenOnline) {
    return (onlineRows ?? []).filter((c) => isVersOnline(c, nu)).filter(uniek)
      .sort(nieuwsteEerst).map((c) => metVersStip(c, nu))
  }
  if (k.onlineEerst) {
    const alle = [...(onlineRows ?? []).filter((c) => isVersOnline(c, nu)), ...(rows ?? [])].filter(uniek)
    const online = alle.filter((c) => isVersOnline(c, nu)).sort(nieuwsteEerst)
    const rest = alle.filter((c) => !isVersOnline(c, nu)).sort(nieuwsteEerst)
    return [...online, ...rest].map((c) => metVersStip(c, nu))
  }
  return (rows ?? []).filter(uniek).map((c) => metVersStip(c, nu))
}

/**
 * 2-11-2026 (Jordy, opdracht E): het kopje van New followers en VIP toont altijd
 * het TOTAAL van het blok, nooit het gefilterde aantal. Daarom hangt dit niet af
 * van de keuze (Online only / Online first): alleen van wat er geladen is en het
 * totaal volgens de server (dat groter kan zijn dan wat er geladen is).
 */
export function kopTotaal(geladen: number, serverTotaal?: unknown): number {
  const server = Number(serverTotaal ?? 0)
  return Math.max(Number(geladen) || 0, Number.isFinite(server) ? server : 0)
}

/**
 * 2-11-2026 (Jordy, opdracht E): de melding boven de lijst als er een filter aan
 * staat, of null. "Online only" verbergt fans; "Online first" alleen de volgorde.
 */
export function actiefFilter(k: VolgerKeuze): string | null {
  if (k.alleenOnline) return 'Showing online only'
  if (k.onlineEerst) return 'Showing online first'
  return null
}

/** Het aantal voor "Online only (n)". */
export function aantalVersOnline(onlineRows: QueueRow[], nu: number = Date.now()): number {
  return new Set((onlineRows ?? []).filter((c) => isVersOnline(c, nu)).map((c) => c.id)).size
}

/**
 * 30-10-2026 (Jordy): het blok VIP. De rijen komen al in de volgorde van het blok
 * binnen (lib/panels.ts vipLijst: wie wacht bovenaan, daarna de rest); die volgorde
 * blijft binnen elke groep staan. Alle VIP-rijen zijn al geladen en dragen hun
 * eigen fan_online/fan_presence_at, dus er is geen aparte online-lijst nodig.
 *
 * - standaard:    de lijst zoals hij was (een verouderde "online" zonder stip)
 * - Online first: de vers-online fans bovenaan, daaronder de rest
 * - Online only:  alleen de vers-online fans
 */
export function vipOnlineLijst(rows: QueueRow[], k: VolgerKeuze, nu: number = Date.now()): QueueRow[] {
  const gezien = new Set<number>()
  const alle = (rows ?? []).filter((c) => !!c && !gezien.has(c.id) && (gezien.add(c.id), true))
  const online = alle.filter((c) => isVersOnline(c, nu))
  const lijst = k.alleenOnline ? online
    : k.onlineEerst ? [...online, ...alle.filter((c) => !isVersOnline(c, nu))]
    : alle
  return lijst.map((c) => metVersStip(c, nu))
}
